import { supabase } from '../lib/supabase';

/**
 * Admin Broadcast Service
 * Broadcasts artist content updates from admin panel to mobile apps in real-time
 * Eliminates polling by pushing changes instantly through Supabase broadcast channels
 */
class AdminBroadcastService {

  /**
   * Broadcast artist profile update
   */
  async broadcastProfileUpdate(artistId, profileData) {
    try {
      const channel = supabase?.channel(`artist_updates_${artistId}`);

      await channel?.send({
        type: 'broadcast',
        event: 'artist_update',
        payload: {
          updateType: 'profile',
          section: 'profile',
          artistId,
          data: {
            fullName: profileData?.full_name || profileData?.fullName,
            email: profileData?.email,
            artistType: profileData?.artist_type || profileData?.artistType,
            bio: profileData?.bio,
            location: profileData?.location,
            website: profileData?.website,
            socialLinks: profileData?.social_links || profileData?.socialLinks,
            status: profileData?.status
          },
          timestamp: new Date()?.toISOString()
        }
      });

      // Clean up channel after broadcast
      await supabase?.removeChannel(channel);

      return { success: true, broadcasted: 'profile' };

    } catch (error) {
      console.error('Broadcast profile update failed:', error);
      return { success: false, error: error?.message };
    }
  }

  /**
   * Broadcast artist works update
   */
  async broadcastWorksUpdate(artistId, workData, operation = 'UPDATE') {
    try {
      const channel = supabase?.channel(`artist_updates_${artistId}`);

      // Get signed URL for image if present
      let imageUrl = null;
      if (workData?.image_path || workData?.imagePath) {
        const path = workData?.image_path || workData?.imagePath;
        const { data } = await supabase?.storage?.from('artist-work-images')?.createSignedUrl(path, 7200);
        imageUrl = data?.signedUrl;
      }

      await channel?.send({
        type: 'broadcast',
        event: 'artist_update',
        payload: {
          updateType: 'works',
          section: 'works',
          operation, // INSERT, UPDATE, DELETE
          artistId,
          data: {
            id: workData?.id,
            title: workData?.title,
            workType: workData?.work_type || workData?.workType,
            imageUrl,
            price: workData?.price,
            includeInShop: workData?.include_in_shop || workData?.includeInShop,
            status: workData?.status,
            sortOrder: workData?.sort_order || workData?.sortOrder
          },
          timestamp: new Date()?.toISOString()
        }
      });

      await supabase?.removeChannel(channel);

      return { success: true, broadcasted: 'works' };

    } catch (error) {
      console.error('Broadcast works update failed:', error);
      return { success: false, error: error?.message };
    }
  }

  /**
   * Broadcast artist events update
   */
  async broadcastEventsUpdate(artistId, eventData, operation = 'UPDATE') {
    try {
      const channel = supabase?.channel(`artist_updates_${artistId}`);

      // Get signed URL for image if present
      let imageUrl = null;
      if (eventData?.image_path || eventData?.imagePath) {
        const path = eventData?.image_path || eventData?.imagePath;
        const { data } = await supabase?.storage?.from('artist-work-images')?.createSignedUrl(path, 7200);
        imageUrl = data?.signedUrl;
      }

      await channel?.send({
        type: 'broadcast',
        event: 'artist_update',
        payload: {
          updateType: 'events',
          section: 'calendar',
          operation, // INSERT, UPDATE, DELETE
          artistId,
          data: {
            id: eventData?.id,
            title: eventData?.title,
            body: eventData?.body,
            eventDate: eventData?.event_date || eventData?.eventDate,
            eventTime: eventData?.event_time || eventData?.eventTime,
            hasUrl: eventData?.has_url || eventData?.hasUrl,
            eventUrl: eventData?.event_url || eventData?.eventUrl,
            imageUrl,
            status: eventData?.status,
            sortOrder: eventData?.sort_order || eventData?.sortOrder
          },
          timestamp: new Date()?.toISOString()
        }
      });

      await supabase?.removeChannel(channel);

      return { success: true, broadcasted: 'events' };

    } catch (error) {
      console.error('Broadcast events update failed:', error);
      return { success: false, error: error?.message };
    }
  }

  /**
   * Broadcast artist about update
   */
  async broadcastAboutUpdate(artistId, aboutData) {
    try {
      const channel = supabase?.channel(`artist_updates_${artistId}`);

      // Get signed URL for header image if present
      let headerImageUrl = null;
      if (aboutData?.header_image_path || aboutData?.headerImagePath) {
        const path = aboutData?.header_image_path || aboutData?.headerImagePath;
        const { data } = await supabase?.storage?.from('artist-profile-images')?.createSignedUrl(path, 7200);
        headerImageUrl = data?.signedUrl;
      }

      await channel?.send({
        type: 'broadcast',
        event: 'artist_update',
        payload: {
          updateType: 'about',
          section: 'about',
          artistId,
          data: {
            text: aboutData?.about_text || aboutData?.text || '',
            bookingContact: aboutData?.booking_contact || aboutData?.bookingContact || {},
            headerImageUrl
          },
          timestamp: new Date()?.toISOString()
        }
      });

      await supabase?.removeChannel(channel);

      return { success: true, broadcasted: 'about' };

    } catch (error) {
      console.error('Broadcast about update failed:', error);
      return { success: false, error: error?.message };
    }
  }

  /**
   * Broadcast artist promotions update
   */
  async broadcastPromotionsUpdate(artistId, promotionsData) {
    try {
      const channel = supabase?.channel(`artist_updates_${artistId}`);

      // Get signed URLs for promotion images
      const imageUrls = {};
      if (promotionsData) {
        const [splashUrl, homeUrl, logoUrl] = await Promise.all([
          promotionsData?.splash_image_path || promotionsData?.splashImagePath 
            ? this.getSignedUrl('artist-promotion-assets', promotionsData?.splash_image_path || promotionsData?.splashImagePath) 
            : null,
          promotionsData?.home_image_path || promotionsData?.homeImagePath 
            ? this.getSignedUrl('artist-promotion-assets', promotionsData?.home_image_path || promotionsData?.homeImagePath) 
            : null,
          promotionsData?.logo_image_path || promotionsData?.logoImagePath 
            ? this.getSignedUrl('artist-promotion-assets', promotionsData?.logo_image_path || promotionsData?.logoImagePath) 
            : null
        ]);

        imageUrls.splashImageUrl = splashUrl;
        imageUrls.homeImageUrl = homeUrl;
        imageUrls.logoImageUrl = logoUrl;
      }

      await channel?.send({
        type: 'broadcast',
        event: 'artist_update',
        payload: {
          updateType: 'promotions',
          section: 'promotions',
          artistId,
          data: {
            featuredItem: promotionsData?.featured_item || promotionsData?.featuredItem || '',
            ...imageUrls
          },
          timestamp: new Date()?.toISOString()
        }
      });

      await supabase?.removeChannel(channel);

      return { success: true, broadcasted: 'promotions' };

    } catch (error) {
      console.error('Broadcast promotions update failed:', error);
      return { success: false, error: error?.message };
    }
  }

  /**
   * Broadcast bulk update (multiple sections changed at once)
   */
  async broadcastBulkUpdate(artistId, updates = {}) {
    try {
      const channel = supabase?.channel(`artist_updates_${artistId}`);

      const sections = Object.keys(updates);
      
      await channel?.send({
        type: 'broadcast',
        event: 'artist_update',
        payload: {
          updateType: 'bulk',
          section: 'multiple',
          artistId,
          sections,
          data: updates,
          timestamp: new Date()?.toISOString()
        }
      });

      await supabase?.removeChannel(channel);

      return { success: true, broadcasted: 'bulk', sections };

    } catch (error) {
      console.error('Broadcast bulk update failed:', error);
      return { success: false, error: error?.message };
    }
  }

  /**
   * Get signed URL helper
   */
  async getSignedUrl(bucket, path) {
    try {
      const { data } = await supabase?.storage?.from(bucket)?.createSignedUrl(path, 7200);
      return data?.signedUrl;
    } catch {
      return null;
    }
  }

  /**
   * Test broadcast functionality
   */
  async testBroadcast(artistId) {
    try {
      const channel = supabase?.channel(`artist_updates_${artistId}`);

      await channel?.send({
        type: 'broadcast',
        event: 'artist_update',
        payload: {
          updateType: 'test',
          section: 'test',
          artistId,
          data: { message: 'Test broadcast successful' },
          timestamp: new Date()?.toISOString()
        }
      });

      await supabase?.removeChannel(channel);

      return { success: true, message: 'Test broadcast sent' };

    } catch (error) {
      return { success: false, error: error?.message };
    }
  }
}

export const adminBroadcastService = new AdminBroadcastService();
export default adminBroadcastService;